import React, { useMemo, useState } from 'react';
import { SparkModalPortal, Modal, BulkActionsBar as SparkBulkActionsBar } from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';

/**
 * BulkActionsBar — Spark-styled action buttons shown when records are selected.
 * Shows: Select All, Review/Unreview, Replace label, Archive/Unarchive, Deselect All.
 *
 * Confirmation modals for archive/unarchive are managed here; all other actions
 * fire their callbacks directly.
 */
export const BulkActionsBar = ({
    selectedCount,
    totalCount,
    selectedRecords = [],
    onSelectAll,
    onDeselectAll,
    onReview,
    onUnreview,
    onReplaceLabel,
    onGenerateLabels,
    onArchive,
    onUnarchive,
    isBenchmark,
    isArchived = false,
    disabled = false,
    taskType,
    isMultilabel = false,
    isSelectAll = false,
}) => {
    const [confirmAction, setConfirmAction] = useState(null); // 'archive' | 'unarchive' | null

    const reviewStatus = useMemo(() => {
        if (isSelectAll) {
            const hasSelection = selectedCount > 0;
            return { hasReviewed: hasSelection, hasUnreviewed: hasSelection };
        }
        if (selectedRecords.length === 0) {
            return { hasReviewed: false, hasUnreviewed: false };
        }
        const isReviewed = record => !!(record?.is_document_reviewed || record?.is_validated);
        const reviewedCount = selectedRecords.filter(record => isReviewed(record)).length;
        const unreviewedCount = selectedRecords.filter(record => !isReviewed(record)).length;
        return { hasReviewed: reviewedCount > 0, hasUnreviewed: unreviewedCount > 0 };
    }, [isSelectAll, selectedCount, selectedRecords]);

    const confirmConfig = {
        archive: {
            title: `Archive ${selectedCount} record${selectedCount > 1 ? 's' : ''}?`,
            description: `This will move ${selectedCount} record${selectedCount > 1 ? 's' : ''} to the archived ${isBenchmark ? 'benchmarks' : 'datasets'} tab.`,
            primaryLabel: 'Archive',
            onConfirm: onArchive,
        },
        unarchive: {
            title: `Unarchive ${selectedCount} record${selectedCount > 1 ? 's' : ''}?`,
            description: `This will move ${selectedCount} record${selectedCount > 1 ? 's' : ''} back to the ${isBenchmark ? 'benchmark' : 'train set'} tab.`,
            primaryLabel: 'Unarchive',
            onConfirm: onUnarchive,
        },
    };

    const active = confirmAction ? confirmConfig[confirmAction] : null;

    // Build the actions array dynamically based on review/archive state and task type
    const actions = useMemo(() => {
        const result = [];

        if (reviewStatus.hasUnreviewed) {
            result.push({
                label: 'Review',
                iconSlug: 'check',
                onClick: onReview,
                disableWhenReadOnly: true,
            });
        }

        if (reviewStatus.hasReviewed) {
            result.push({
                label: 'Unreview',
                iconSlug: 'close',
                onClick: onUnreview,
                disableWhenReadOnly: true,
            });
        }

        if (
            taskType === TaskType.classification ||
            taskType === TaskType.NER ||
            taskType === TaskType.NER_WITH_RELATIONS
        ) {
            result.push({
                label: 'Replace',
                iconSlug: 'tag',
                onClick: onReplaceLabel,
                disableWhenReadOnly: true,
            });
        }

        if (isArchived) {
            result.push({
                label: 'Unarchive',
                iconSlug: 'check-circle',
                tooltip: 'Unarchive examples',
                onClick: () => setConfirmAction('unarchive'),
                disableWhenReadOnly: true,
            });
        } else {
            result.push({
                label: 'Archive',
                iconSlug: 'trash',
                tooltip: 'Archive examples',
                onClick: () => setConfirmAction('archive'),
                disableWhenReadOnly: true,
            });
        }

        return result;
    }, [reviewStatus, taskType, isArchived, onReview, onUnreview, onReplaceLabel]);

    return (
        <>
            <SparkBulkActionsBar
                selectedCount={selectedCount}
                totalCount={totalCount}
                onSelectAll={onSelectAll}
                onDeselectAll={onDeselectAll}
                actions={actions}
                disabled={disabled}
            />

            <SparkModalPortal open={!!confirmAction} onClose={() => setConfirmAction(null)} size="text">
                {active && (
                    <Modal
                        feedbackType="warning"
                        title={active.title}
                        showSubtitle={false}
                        showScrollBar={false}
                        fixedHeight={false}
                        primaryLabel={active.primaryLabel}
                        primaryDisableWhenReadOnly
                        tertiaryLabel="Cancel"
                        onPrimaryClick={() => {
                            active.onConfirm();
                            setConfirmAction(null);
                        }}
                        onTertiaryClick={() => setConfirmAction(null)}
                        onCloseClick={() => setConfirmAction(null)}
                    >
                        <p>{active.description}</p>
                    </Modal>
                )}
            </SparkModalPortal>
        </>
    );
};
